import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  CircularProgress,
  Chip,
  Tooltip,
  IconButton,
  Button,
} from '@mui/material';
import {
  CloudUpload,
  TrendingUp,
  BoltOutlined,
  NotificationsActive,
  Refresh,
  HistoryToggleOff,
  VerifiedUser,
  FilterList,
  KeyboardDoubleArrowDown,
  CheckCircleOutline,
  HighlightOff,
  CameraAltOutlined,
  FileDownloadOutlined,
  OpenInNewOutlined,
} from '@mui/icons-material';
import { useAppSession } from '../../contexts/AppSessionContext';
import { useAccountStore } from '../../components/Stores/accountStore';
import BatchDetailModal from './BatchDetailModal';
import {
  getActivityTimeline,
  TimelineEvent,
  EventCategory,
} from '../../services/activityService';

// ─── Category visual config ─────────────────────────────────────────

const HERO_CATEGORIES = new Set<EventCategory>(['import', 'review']);

const ICON_COLOR = '#4b5563'; // gray-600 for all icons

const CATEGORY_CONFIG: Record<EventCategory, {
  label: string;
  icon: React.ReactNode;
  chipColor: string;
  chipBg: string;
}> = {
  import: {
    label: 'Imports',
    icon: <CloudUpload sx={{ fontSize: 14 }} />,
    chipColor: '#3b82f6',
    chipBg: '#eff6ff',
  },
  review: {
    label: 'Reviews',
    icon: <VerifiedUser sx={{ fontSize: 14 }} />,
    chipColor: '#10b981',
    chipBg: '#ecfdf5',
  },
  fetch: {
    label: 'Price Fetches',
    icon: <TrendingUp sx={{ fontSize: 14 }} />,
    chipColor: '#8b5cf6',
    chipBg: '#f5f3ff',
  },
  pipeline: {
    label: 'Pipeline',
    icon: <BoltOutlined sx={{ fontSize: 14 }} />,
    chipColor: '#f59e0b',
    chipBg: '#fffbeb',
  },
  notification: {
    label: 'Notifications',
    icon: <NotificationsActive sx={{ fontSize: 14 }} />,
    chipColor: '#ef4444',
    chipBg: '#fef2f2',
  },
  snapshot: {
    label: 'Snapshots',
    icon: <CameraAltOutlined sx={{ fontSize: 14 }} />,
    chipColor: '#6366f1',
    chipBg: '#eef2ff',
  },
};

const STATUS_COLOR: Record<string, string> = {
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#94a3b8',
  pending: '#94a3b8',
};

// ─── Import-specific review status → border color ────────────────────

const REVIEW_BORDER: Record<string, string> = {
  approved: '#10b981',   // green
  pending:  '#f59e0b',   // orange
  rejected: '#ef4444',   // red
};

function getCircleBorder(event: TimelineEvent): string {
  if (event.category === 'import' && event.detail?.reviewStatus) {
    return REVIEW_BORDER[event.detail.reviewStatus] || STATUS_COLOR[event.status] || '#94a3b8';
  }
  return STATUS_COLOR[event.status] || '#94a3b8';
}

// ─── Helpers ─────────────────────────────────────────────────────────

function groupByDay(events: TimelineEvent[]): Map<string, TimelineEvent[]> {
  const groups = new Map<string, TimelineEvent[]>();
  for (const ev of events) {
    const date = new Date(ev.timestamp);
    const key = date.toLocaleDateString('en-IL', {
      timeZone: 'Asia/Jerusalem',
      weekday: 'long',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    const list = groups.get(key) || [];
    list.push(ev);
    groups.set(key, list);
  }
  return groups;
}

/** Collapse consecutive same-category events into groups */
type DisplayItem =
  | { type: 'single'; event: TimelineEvent }
  | { type: 'collapsed'; events: TimelineEvent[]; category: EventCategory };

function collapseSequential(events: TimelineEvent[]): DisplayItem[] {
  const items: DisplayItem[] = [];
  let i = 0;
  while (i < events.length) {
    const ev = events[i];
    // Never collapse hero events (imports/reviews)
    if (HERO_CATEGORIES.has(ev.category)) {
      items.push({ type: 'single', event: ev });
      i++;
      continue;
    }
    // Collect consecutive same-category
    let j = i + 1;
    while (j < events.length && events[j].category === ev.category) j++;
    const run = events.slice(i, j);
    if (run.length >= 3) {
      items.push({ type: 'collapsed', events: run, category: ev.category });
    } else {
      for (const e of run) items.push({ type: 'single', event: e });
    }
    i = j;
  }
  return items;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-IL', {
    timeZone: 'Asia/Jerusalem',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function isToday(dateLabel: string): boolean {
  const today = new Date().toLocaleDateString('en-IL', {
    timeZone: 'Asia/Jerusalem',
    weekday: 'long',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  return dateLabel === today;
}

/** Find the latest approved import per batch type + latest successful fetch */
function findLatestDataEvents(events: TimelineEvent[]): Set<string> {
  const latestIds = new Set<string>();
  const foundImportType = new Set<string>(); // supply, demand, hedge, mixed
  let foundFetch = false;
  for (const ev of events) {
    if (ev.category === 'import' && ev.detail?.reviewStatus === 'approved') {
      const batchType = ev.detail.type as string;
      if (batchType && !foundImportType.has(batchType)) {
        foundImportType.add(batchType);
        latestIds.add(ev.id);
        // mixed covers both supply & demand, mark them too
        if (batchType === 'mixed') {
          foundImportType.add('supply');
          foundImportType.add('demand');
        }
      }
    }
    if (ev.category === 'fetch' && !foundFetch && (ev.status === 'success' || ev.status === 'warning')) {
      foundFetch = true;
      latestIds.add(ev.id);
    }
  }
  return latestIds;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// Module-level callback for opening batch detail modal (set by main component)
let openBatchDetail: (batchId: number) => void = () => {};

// ─── Detail renderers (shown on expand) ─────────────────────────────

function ImportDetail({ detail }: { detail: Record<string, any> }) {
  const counts = [];
  if (detail.supplyCount > 0) counts.push(`${detail.supplyCount} supply`);
  if (detail.demandCount > 0) counts.push(`${detail.demandCount} demand`);
  if (detail.hedgeCount > 0) counts.push(`${detail.hedgeCount} hedge`);
  return (
    <div className="text-xs text-gray-500 space-y-1.5 mt-2 pl-1">
      {counts.length > 0 && (
        <div className="flex gap-3">
          {counts.map(c => (
            <span key={c} className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{c}</span>
          ))}
        </div>
      )}
      {detail.stagingCount > 0 && <div>{detail.stagingCount} staging rows parsed</div>}
      {detail.errorSummary && (
        <div className="text-red-500 bg-red-50 px-2 py-1 rounded">Error: {detail.errorSummary}</div>
      )}
      <div>
        Status: <span className={
          detail.reviewStatus === 'approved' ? 'text-green-600 font-medium' :
          detail.reviewStatus === 'rejected' ? 'text-red-500 font-medium' :
          'text-yellow-600 font-medium'
        }>{detail.reviewStatus}</span>
        {detail.reviewStatus === 'approved' && (
          <span className="text-gray-400 ml-1">(replaces previous data)</span>
        )}
      </div>
      <div className="flex items-center gap-3 mt-2">
        <Tooltip title="Download source file (coming soon)" arrow>
          <span>
            <IconButton disabled size="small" sx={{ color: '#94a3b8' }}>
              <FileDownloadOutlined sx={{ fontSize: 14 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="View batch details" arrow>
          <IconButton
            size="small"
            sx={{ color: '#64748b', '&:hover': { color: '#3b82f6' } }}
            onClick={(e) => { e.stopPropagation(); openBatchDetail(detail.batchId); }}
          >
            <OpenInNewOutlined sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
}

function ReviewDetail({ detail }: { detail: Record<string, any> }) {
  return (
    <div className="text-xs text-gray-500 space-y-1 mt-2 pl-1">
      <div>Batch #{detail.batchId} &middot; {detail.type}</div>
      {detail.filename && <div className="text-gray-400">{detail.filename}</div>}
      {detail.reviewComment && (
        <div className="italic text-gray-400 bg-gray-50 px-2 py-1 rounded">
          &ldquo;{detail.reviewComment}&rdquo;
        </div>
      )}
    </div>
  );
}

function FetchDetail({ detail }: { detail: Record<string, any> }) {
  const dur = detail.durationSec ? `${Math.round(detail.durationSec)}s` : null;
  return (
    <div className="text-xs text-gray-500 mt-2 pl-1 flex items-center gap-3">
      <span className="text-green-600">{detail.successCount ?? 0} ok</span>
      <span className="text-red-500">{detail.failedCount ?? 0} fail</span>
      <span className="text-gray-400">{detail.skippedCount ?? 0} skip</span>
      {dur && <span className="text-gray-400">&middot; {dur}</span>}
    </div>
  );
}

function PipelineDetail({ detail }: { detail: Record<string, any> }) {
  const dur = detail.durationMs ? `${(detail.durationMs / 1000).toFixed(1)}s` : null;
  return (
    <div className="text-xs text-gray-500 space-y-1 mt-2 pl-1">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-0.5">
          {detail.step1ExposureOk
            ? <CheckCircleOutline sx={{ fontSize: 12, color: '#10b981' }} />
            : <HighlightOff sx={{ fontSize: 12, color: '#ef4444' }} />}
          Exposure
        </span>
        <span className="inline-flex items-center gap-0.5">
          {detail.step2DerivationOk
            ? <CheckCircleOutline sx={{ fontSize: 12, color: '#10b981' }} />
            : <HighlightOff sx={{ fontSize: 12, color: '#ef4444' }} />}
          Derivation
        </span>
        <span className="inline-flex items-center gap-0.5">
          {detail.step3EvalOk
            ? <CheckCircleOutline sx={{ fontSize: 12, color: '#10b981' }} />
            : <HighlightOff sx={{ fontSize: 12, color: '#ef4444' }} />}
          Rules
        </span>
      </div>
      <div className="flex items-center gap-2 text-gray-400">
        {detail.trigger && <span>{detail.trigger}</span>}
        {dur && <span>&middot; {dur}</span>}
        {detail.notificationsCreated > 0 && (
          <span>&middot; {detail.notificationsCreated} alert(s)</span>
        )}
      </div>
      {detail.error && <div className="text-red-500">Error: {detail.error}</div>}
    </div>
  );
}

function NotificationDetail({ detail }: { detail: Record<string, any> }) {
  return (
    <div className="text-xs text-gray-500 space-y-1 mt-2 pl-1">
      {detail.formulaCode && <div>Formula: <span className="font-medium">{detail.formulaCode}</span></div>}
      <div>Severity: <span className={
        detail.severity === 'critical' ? 'text-red-500 font-medium' :
        detail.severity === 'warning' ? 'text-yellow-600 font-medium' :
        'text-gray-500'
      }>{detail.severity}</span></div>
    </div>
  );
}

function SnapshotDetail({ detail }: { detail: Record<string, any> }) {
  return (
    <div className="text-xs text-gray-500 space-y-1 mt-2 pl-1">
      <div>Date: <span className="font-medium">{detail.snapshotDate}</span></div>
      <div>{detail.hasData ? 'Full state captured' : 'No changes from previous day'}</div>
      {/* Phase 4: "Restore this snapshot" / "View state" button */}
    </div>
  );
}

const DETAIL_RENDERERS: Record<EventCategory, React.FC<{ detail: Record<string, any> }>> = {
  import: ImportDetail,
  review: ReviewDetail,
  fetch: FetchDetail,
  pipeline: PipelineDetail,
  notification: NotificationDetail,
  snapshot: SnapshotDetail,
};

// ─── Timeline Node ───────────────────────────────────────────────────

function TimelineNode({
  event,
  isLast,
  isLatestInCategory,
  nodeRef,
}: {
  event: TimelineEvent;
  isLast: boolean;
  isLatestInCategory: boolean;
  nodeRef?: React.Ref<HTMLDivElement>;
}) {
  const [expanded, setExpanded] = useState(false);
  const cfg = CATEGORY_CONFIG[event.category];
  const isHero = HERO_CATEGORIES.has(event.category);
  const borderColor = getCircleBorder(event);
  const circleSize = 28;

  return (
    <div ref={nodeRef} className="flex group" style={{ minHeight: 48 }}>
      {/* Left: time label */}
      <div className="w-14 flex-shrink-0 text-right pr-3" style={{ paddingTop: 4 }}>
        <span className={`text-xs ${isHero ? 'text-gray-500 font-medium' : 'text-gray-400'}`}>
          {formatTime(event.timestamp)}
        </span>
      </div>

      {/* Center: spine + circled icon */}
      <div className="flex flex-col items-center flex-shrink-0" style={{ width: 36 }}>
        <Tooltip title={cfg.label} placement="left" arrow>
          <div
            className="rounded-full flex-shrink-0 cursor-pointer flex items-center justify-center transition-transform group-hover:scale-125"
            style={{
              width: circleSize,
              height: circleSize,
              backgroundColor: 'white',
              border: `2px solid ${borderColor}`,
              color: ICON_COLOR,
            }}
            onClick={() => setExpanded(!expanded)}
          >
            {cfg.icon}
          </div>
        </Tooltip>
        {!isLast && (
          <div className="flex-1 w-px bg-gray-200" style={{ minHeight: 8 }} />
        )}
      </div>

      {/* Right: content */}
      <div
        className="flex-1 min-w-0 pl-3 cursor-pointer pb-4"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <span className={`text-sm ${isHero ? 'font-semibold text-gray-800' : 'text-gray-600'}`}>
            {event.title}
          </span>
          {isLatestInCategory && (
            <>
              <span className="text-xs text-green-700 font-medium bg-green-50 px-1.5 py-0.5 rounded">
                latest data
              </span>
              <span className="text-xs text-gray-400">
                {timeAgo(event.timestamp)}
              </span>
            </>
          )}
          {event.status === 'error' && event.category !== 'notification' && (
            <span className="text-xs text-red-500 font-medium bg-red-50 px-1.5 py-0.5 rounded">
              failed
            </span>
          )}
        </div>
        <div className={`text-xs mt-0.5 ${isHero ? 'text-gray-500' : 'text-gray-400'}`}>
          {event.subtitle}
        </div>
        {event.actor && event.actor !== 'System' && (
          <div className="text-xs text-gray-400 mt-0.5">by {event.actor}</div>
        )}
        <div
          style={{
            maxHeight: expanded ? 300 : 0,
            opacity: expanded ? 1 : 0,
            overflow: 'hidden',
            transition: 'max-height 250ms ease-in-out, opacity 200ms ease-in-out',
          }}
        >
          <div className="mt-1.5 pb-2 mb-1 border-b border-gray-100">
            {DETAIL_RENDERERS[event.category]?.({ detail: event.detail })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Collapsed Group Node ────────────────────────────────────────────

function CollapsedGroup({
  items,
  category,
  isLast,
}: {
  items: TimelineEvent[];
  category: EventCategory;
  isLast: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [animating, setAnimating] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const cfg = CATEGORY_CONFIG[category];
  const first = items[0];
  const last = items[items.length - 1];

  const toggle = () => {
    setAnimating(true);
    setExpanded(prev => !prev);
    setTimeout(() => setAnimating(false), 350);
  };

  const collapse = () => {
    if (!expanded || animating) return;
    setAnimating(true);
    setExpanded(false);
    setTimeout(() => setAnimating(false), 350);
  };

  return (
    <div onMouseLeave={collapse}>
      {/* Collapsed summary — fades out when expanded */}
      <div
        style={{
          maxHeight: expanded ? 0 : 80,
          opacity: expanded ? 0 : 1,
          overflow: 'hidden',
          transition: 'max-height 300ms ease-in-out, opacity 200ms ease-in-out',
        }}
      >
        <div className="flex group" style={{ minHeight: 48 }}>
          <div className="w-14 flex-shrink-0 text-right pr-3" style={{ paddingTop: 6 }}>
            <span className="text-xs text-gray-400">
              {formatTime(last.timestamp)}
            </span>
          </div>
          <div className="flex flex-col items-center flex-shrink-0" style={{ width: 36 }}>
            <Tooltip title={`${items.length} ${cfg.label} — click to expand`} placement="left" arrow>
              <div
                className="relative cursor-pointer group/stack"
                style={{ width: 36, height: 36 }}
                onClick={toggle}
              >
                {/* Back circle (right) */}
                {items.length >= 3 && (
                  <div
                    className="absolute rounded-full flex items-center justify-center transition-all duration-200 ease-out group-hover/stack:translate-x-1.5"
                    style={{
                      width: 28,
                      height: 28,
                      backgroundColor: 'white',
                      border: `2px solid ${STATUS_COLOR[items[2].status] || '#94a3b8'}`,
                      color: ICON_COLOR,
                      top: 4,
                      left: 'calc(50% - 14px + 4px)',
                      zIndex: 1,
                    }}
                  />
                )}
                {/* Middle circle (center) */}
                {items.length >= 2 && (
                  <div
                    className="absolute rounded-full flex items-center justify-center transition-all duration-200 ease-out"
                    style={{
                      width: 28,
                      height: 28,
                      backgroundColor: 'white',
                      border: `2px solid ${STATUS_COLOR[items[1].status] || '#94a3b8'}`,
                      color: ICON_COLOR,
                      top: 4,
                      left: 'calc(50% - 14px)',
                      zIndex: 2,
                    }}
                  />
                )}
                {/* Front circle (left) */}
                <div
                  className="absolute rounded-full flex items-center justify-center transition-all duration-200 ease-out group-hover/stack:-translate-x-1.5"
                  style={{
                    width: 28,
                    height: 28,
                    backgroundColor: 'white',
                    border: `2px solid ${STATUS_COLOR[items[0].status] || '#94a3b8'}`,
                    color: ICON_COLOR,
                    top: 4,
                    left: 'calc(50% - 14px - 4px)',
                    zIndex: 3,
                  }}
                >
                  {cfg.icon}
                </div>
              </div>
            </Tooltip>
            {!isLast && (
              <div className="flex-1 w-px bg-gray-200" style={{ minHeight: 8 }} />
            )}
          </div>
          <div className="flex-1 min-w-0 pl-3 cursor-pointer pb-2" onClick={toggle}>
            <div className="flex items-center gap-2 pt-0.5">
              <span className="text-xs text-gray-500">
                {items.length} {cfg.label.toLowerCase()}
              </span>
              <span className="text-xs text-gray-400">
                {formatTime(last.timestamp)} — {formatTime(first.timestamp)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded items — slides in */}
      <div
        ref={contentRef}
        style={{
          maxHeight: expanded ? items.length * 80 : 0,
          opacity: expanded ? 1 : 0,
          overflow: expanded ? 'visible' : 'hidden',
          transition: 'max-height 300ms ease-in-out, opacity 250ms ease-in-out 50ms',
        }}
      >
        {items.map((ev, i) => (
          <TimelineNode
            key={ev.id}
            event={ev}
            isLast={isLast && i === items.length - 1}
            isLatestInCategory={false}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────

const ALL_CATEGORIES: EventCategory[] = ['import', 'review', 'fetch', 'pipeline', 'notification', 'snapshot'];

const ActivityTimeline: React.FC = () => {
  const { user: sessionUser } = useAppSession();
  const { account } = useAccountStore();
  const accountId = account?.id
    ? parseInt(account.id)
    : sessionUser?.accountId
      ? parseInt(sessionUser.accountId)
      : undefined;

  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<Set<EventCategory>>(new Set());
  const [detailBatchId, setDetailBatchId] = useState<number | null>(null);
  openBatchDetail = setDetailBatchId;


  const fetchEvents = useCallback(async (before?: string) => {
    if (!accountId) return;
    try {
      const isLoadMore = !!before;
      if (isLoadMore) setLoadingMore(true);
      else setLoading(true);

      const categories = activeFilters.size > 0
        ? Array.from(activeFilters)
        : undefined;

      const res = await getActivityTimeline(accountId, {
        limit: 50,
        before,
        categories,
      });

      if (isLoadMore) {
        setEvents(prev => [...prev, ...res.events]);
      } else {
        setEvents(res.events);
      }
      setHasMore(res.hasMore);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load activity');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [accountId, activeFilters]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const latestDataIds = useMemo(() => findLatestDataEvents(events), [events]);

  const toggleFilter = (cat: EventCategory) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const loadMore = () => {
    if (events.length === 0 || loadingMore) return;
    const lastTs = events[events.length - 1].timestamp;
    fetchEvents(lastTs);
  };

  // Latest approved import per batch type + latest fetch
  const latestImports = useMemo(() => {
    const map = new Map<string, TimelineEvent>();
    for (const ev of events) {
      if (ev.category === 'import' && ev.detail?.reviewStatus === 'approved') {
        const t = ev.detail.type as string;
        if (t && !map.has(t)) map.set(t, ev);
        if (t === 'mixed') { if (!map.has('supply')) map.set('supply', ev); if (!map.has('demand')) map.set('demand', ev); }
      }
      if (ev.category === 'fetch' && !map.has('prices') && (ev.status === 'success' || ev.status === 'warning')) {
        map.set('prices', ev);
      }
    }
    return map;
  }, [events]);

  const nodeRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const scrollToEvent = (id: string) => {
    nodeRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const dayGroups = groupByDay(events);

  return (
    <div className="h-full overflow-y-auto bg-gray-50 p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div>
            <h1 className="text-xl font-semibold text-gray-800 text-left">Activity</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Track where your data comes from and what happened to it
            </p>
          </div>
          <div className="flex items-center gap-1">
            <Tooltip title="Browse snapshots (coming soon)">
              <span>
                <IconButton disabled size="small" sx={{ color: '#94a3b8' }}>
                  <HistoryToggleOff sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton onClick={() => fetchEvents()} size="small" sx={{ color: '#64748b' }}>
                <Refresh sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </div>
        </div>
        {/* Latest data summary */}
        {latestImports.size > 0 && !loading && (
          <div className="mb-5 p-3 bg-white rounded-lg border border-gray-100 space-y-1.5">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Current data sources</div>
            {(['supply', 'demand', 'hedge', 'prices'] as const).map(type => {
              const ev = latestImports.get(type);
              if (!ev) return null;
              const label = type === 'prices' ? 'Prices' : type.charAt(0).toUpperCase() + type.slice(1);
              return (
                <div
                  key={type}
                  className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded px-2 py-1 -mx-2 transition-colors"
                  onClick={() => scrollToEvent(ev.id)}
                >
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-400" />
                    <span className="text-sm text-gray-700 font-medium">{label}</span>
                    <span className="text-xs text-gray-400 truncate max-w-[180px]">{ev.subtitle || ev.title}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-400 mr-1">{timeAgo(ev.timestamp)}</span>
                    {type !== 'prices' && (
                      <>
                        <Tooltip title="Download source file (coming soon)" arrow>
                          <span>
                            <IconButton disabled size="small" sx={{ padding: '2px', color: '#94a3b8' }}>
                              <FileDownloadOutlined sx={{ fontSize: 14 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="View batch details" arrow>
                          <IconButton
                            size="small"
                            sx={{ padding: '2px', color: '#64748b', '&:hover': { color: '#3b82f6' } }}
                            onClick={(e) => { e.stopPropagation(); setDetailBatchId(ev.detail?.batchId); }}
                          >
                            <OpenInNewOutlined sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Filter chips */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <FilterList sx={{ fontSize: 16, color: '#94a3b8' }} />
          {ALL_CATEGORIES.map(cat => {
            const cfg = CATEGORY_CONFIG[cat];
            const isActive = activeFilters.has(cat);
            return (
              <Chip
                key={cat}
                label={cfg.label}
                size="small"
                onClick={() => toggleFilter(cat)}
                variant={isActive ? 'filled' : 'outlined'}
                sx={{
                  borderColor: isActive ? cfg.chipColor : '#e2e8f0',
                  backgroundColor: isActive ? cfg.chipBg : 'transparent',
                  color: isActive ? cfg.chipColor : '#64748b',
                  fontWeight: isActive ? 600 : 400,
                  fontSize: '0.75rem',
                  height: 26,
                  '&:hover': { backgroundColor: cfg.chipBg },
                }}
              />
            );
          })}
          {activeFilters.size > 0 && (
            <Chip
              label="Clear"
              size="small"
              onClick={() => setActiveFilters(new Set())}
              variant="outlined"
              sx={{ borderColor: '#e2e8f0', color: '#94a3b8', height: 26, fontSize: '0.75rem' }}
            />
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <CircularProgress size={28} sx={{ color: '#94a3b8' }} />
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-red-500 text-sm">{error}</p>
            <Button onClick={() => fetchEvents()} size="small" sx={{ mt: 1, color: '#64748b' }}>
              Retry
            </Button>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-400 text-sm">No activity found</p>
          </div>
        ) : (
          <div>
            {(() => {
              // Flatten all day groups into a single list with day headers interspersed
              const allDayEntries = Array.from(dayGroups.entries());
              // Count total events across all days for isLast calculation
              let globalTotal = 0;
              const dayDisplayItems = allDayEntries.map(([, dayEvents]) => {
                const items = collapseSequential(dayEvents);
                const count = items.reduce(
                  (n, it) => n + (it.type === 'collapsed' ? it.events.length : 1), 0,
                );
                globalTotal += count;
                return { items, count };
              });
              let globalIdx = 0;

              return allDayEntries.map(([dayLabel], dayIndex) => {
                const { items: displayItems } = dayDisplayItems[dayIndex];

                return (
                  <div key={dayLabel}>
                    {/* Day header — spine runs behind it */}
                    <div className="flex items-center" style={{ minHeight: 32 }}>
                      <div className="w-14 flex-shrink-0" />
                      <div className="flex flex-col items-center flex-shrink-0" style={{ width: 36 }}>
                        <div className="w-px bg-gray-200" style={{ height: 8 }} />
                        <div className="w-2 h-2 rounded-full bg-gray-300" />
                        <div className="w-px bg-gray-200" style={{ height: 8 }} />
                      </div>
                      <div className="flex-1 pl-3">
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                          {isToday(dayLabel) ? `Today — ${dayLabel}` : dayLabel}
                        </span>
                      </div>
                    </div>

                    {/* Events */}
                    {displayItems.map((item) => {
                      if (item.type === 'collapsed') {
                        const isLast = globalIdx + item.events.length >= globalTotal;
                        globalIdx += item.events.length;
                        return (
                          <CollapsedGroup
                            key={`grp-${item.events[0].id}`}
                            items={item.events}
                            category={item.category}
                            isLast={isLast}
                          />
                        );
                      }
                      const ev = item.event;
                      const isLast = globalIdx + 1 >= globalTotal;
                      globalIdx++;
                      const isLatest = latestDataIds.has(ev.id);
                      return (
                        <TimelineNode
                          key={ev.id}
                          event={ev}
                          isLast={isLast}
                          isLatestInCategory={isLatest}
                          nodeRef={(el) => { if (el) nodeRefs.current[ev.id] = el; }}
                        />
                      );
                    })}
                  </div>
                );
              });
            })()}

            {/* Load more */}
            {hasMore && (
              <div className="flex justify-center py-4">
                <Button
                  onClick={loadMore}
                  disabled={loadingMore}
                  size="small"
                  sx={{ color: '#64748b' }}
                >
                  {loadingMore ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
                  Load more
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Phase 2 placeholder: Validation results */}
        {/* Phase 3 placeholder: User action events */}
        {/* Phase 4 placeholder: Time machine date picker */}
      </div>
      <BatchDetailModal
        open={detailBatchId !== null}
        batchId={detailBatchId}
        onClose={() => setDetailBatchId(null)}
      />
    </div>
  );
};

export default ActivityTimeline;
